"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay, isWithinInterval, isBefore, isAfter, subMonths, startOfDay } from "date-fns";
import { DateRange } from "react-day-picker";

export type CalendarProps = {
  className?: string;
  classNames?: any;
  showOutsideDays?: boolean;
  prices?: Record<string, number>;
  mode?: "range" | "single";
  selected?: DateRange | Date | undefined;
  onSelect?: (range: any) => void;
  numberOfMonths?: number;
  defaultMonth?: Date;
  modifiers?: any;
  modifiersClassNames?: any;
  minDate?: Date; // Minimum date that can be selected (usually today)
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  prices = {},
  mode = "single",
  selected,
  onSelect,
  numberOfMonths = 1,
  defaultMonth,
  modifiers = {},
  modifiersClassNames = {},
  minDate, // Minimum selectable date (defaults to today)
  ...props
}: CalendarProps) {
  const [currentMonth, setCurrentMonth] = React.useState<Date>(defaultMonth || new Date());
  const [hoverDate, setHoverDate] = React.useState<Date | null>(null);
  const [isMobile, setIsMobile] = React.useState(false);

  // Default minimum date to today if not provided
  const today = React.useMemo(() => startOfDay(new Date()), []);
  const minimumDate = minDate ? startOfDay(minDate) : today;

  // Detect mobile screen size
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const months = React.useMemo(() => {
    const ms = [];
    // Show 1 month on mobile, 2 on desktop
    const monthsToShow = isMobile ? 1 : numberOfMonths;
    for (let i = 0; i < monthsToShow; i++) {
      ms.push(addMonths(currentMonth, i));
    }
    return ms;
  }, [currentMonth, numberOfMonths, isMobile]);

  const handleDayClick = (day: Date) => {
    const normalizedDay = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);

    // Prevent selecting dates before the minimum date (today)
    if (isBefore(normalizedDay, minimumDate)) {
      return;
    }

    if (mode === "range" && onSelect) {
      const currentRange = selected as DateRange;

      if (currentRange?.from && currentRange?.to) {
        onSelect({ from: normalizedDay, to: undefined });
        return;
      }

      if (currentRange?.from && !currentRange.to) {
        const fromDate = new Date(currentRange.from.getFullYear(), currentRange.from.getMonth(), currentRange.from.getDate(), 0, 0, 0, 0);

        if (isSameDay(normalizedDay, fromDate)) {
          onSelect({ from: currentRange.from, to: normalizedDay });
          return;
        }

        if (isBefore(normalizedDay, fromDate)) {
          // Don't allow selecting a date before the minimum date
          if (isBefore(normalizedDay, minimumDate)) {
            return;
          }
          onSelect({ from: normalizedDay, to: undefined });
        } else {
          onSelect({ from: currentRange.from, to: normalizedDay });
        }
      } else {
        onSelect({ from: normalizedDay, to: undefined });
      }
    } else if (mode === "single" && onSelect) {
       onSelect(normalizedDay);
    }
  };

  const renderMonth = (monthDate: Date, index: number) => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const weekDays = isMobile
      ? ["S", "M", "T", "W", "T", "F", "S"]
      : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const days = [];
    let day = startDate;

    // Generate all days
    while (day <= endDate) {
      const weekDaysArray = [];
      for (let i = 0; i < 7; i++) {
        const normalizedLoopDay = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
        const formattedDate = format(day, "d");
        const cloneDay = normalizedLoopDay;
        const dateStr = format(day, "yyyy-MM-dd");
        const price = prices[dateStr];

        let priceDisplay = "";
        if (price) {
          if (price >= 1000000) {
            priceDisplay = (price / 1000000).toFixed(1) + "M";
          } else {
            priceDisplay = (price / 1000).toFixed(0) + "k";
          }
        }

        const isSelected = mode === "range"
            ? (selected as DateRange)?.from && isSameDay(normalizedLoopDay, (selected as DateRange).from!)
              || (selected as DateRange)?.to && isSameDay(normalizedLoopDay, (selected as DateRange).to!)
            : isSameDay(normalizedLoopDay, selected as Date);

        const isInRange = mode === "range" && (selected as DateRange)?.from && (selected as DateRange)?.to
            ? isWithinInterval(normalizedLoopDay, { start: (selected as DateRange).from!, end: (selected as DateRange).to! })
            : false;

        const isRangeStart = mode === "range" && (selected as DateRange)?.from && isSameDay(normalizedLoopDay, (selected as DateRange).from!);
        const isRangeEnd = mode === "range" && (selected as DateRange)?.to && isSameDay(normalizedLoopDay, (selected as DateRange).to!);
        const isSingleDayRange = mode === "range" && (selected as DateRange)?.from && (selected as DateRange)?.to && isSameDay((selected as DateRange).from!, (selected as DateRange).to!);
        const isRangeMiddle = isInRange && !isRangeStart && !isRangeEnd;

        const isToday = isSameDay(normalizedLoopDay, new Date());
        const isOutsideMonth = !isSameMonth(normalizedLoopDay, monthStart);
        const isHoliday = modifiers.holiday && modifiers.holiday.some((h: Date) => isSameDay(h, normalizedLoopDay));

        // Check if date is before minimum date (disabled)
        const isDisabledDate = isBefore(normalizedLoopDay, minimumDate);

        const hasPartialSelection = mode === "range" && (selected as DateRange)?.from && !(selected as DateRange)?.to;
        const isPartialFromDate = hasPartialSelection && (selected as DateRange)?.from && isSameDay(normalizedLoopDay, (selected as DateRange).from!);

        const showHoverPreview = hasPartialSelection &&
          hoverDate &&
          isAfter(hoverDate, (selected as DateRange).from!) &&
          isBefore(normalizedLoopDay, hoverDate) &&
          isAfter(normalizedLoopDay, (selected as DateRange).from!);

        weekDaysArray.push(
          <div
            key={day.toString()}
            onMouseEnter={() => !isDisabledDate && setHoverDate(normalizedLoopDay)}
            onMouseLeave={() => setHoverDate(null)}
            className="relative flex-1 min-w-0"
          >
            <button
              className={cn(
                // Base styles - compact sizing
                isMobile
                  ? "h-10 w-full text-xs rounded-lg"
                  : "h-9 w-full rounded-lg",
                "p-0 font-normal flex flex-col items-center justify-center gap-0 group relative overflow-hidden transition-all duration-150",
                // Base text color for normal dates
                "text-gray-800",
                // Disabled state for past dates
                isDisabledDate && "!text-gray-300 opacity-40 cursor-not-allowed",
                // Partial selection - amber/orange
                !isDisabledDate && isPartialFromDate && "bg-amber-500 text-white shadow-md",
                // Complete selection - indigo/blue
                !isDisabledDate && isSelected && !isPartialFromDate && "bg-indigo-600 text-white shadow-md",
                // Range middle
                !isDisabledDate && isRangeMiddle && "bg-indigo-50 text-indigo-700 rounded-none",
                // Hover preview
                !isDisabledDate && showHoverPreview && "bg-indigo-50 border border-dashed border-indigo-300 rounded-lg text-indigo-700",
                // Default hover
                !isDisabledDate && !isSelected && !isInRange && !isRangeMiddle && !isPartialFromDate && !showHoverPreview && "hover:bg-gray-50",
                // Outside month
                !isDisabledDate && (isOutsideMonth && !isSelected && !isInRange) && "text-gray-200",
                // Today
                !isDisabledDate && isToday && !isSelected && !isInRange && !isPartialFromDate && "bg-amber-50 text-amber-600 font-semibold border border-amber-200",
                // Holiday
                !isDisabledDate && isHoliday && modifiersClassNames.holiday
              )}
              disabled={isDisabledDate || (isOutsideMonth && !showOutsideDays)}
              onClick={() => !isDisabledDate && handleDayClick(cloneDay)}
            >
              {/* Range connectors */}
              {isRangeStart && !isSingleDayRange && (
                <div className="absolute top-1/2 -translate-y-1/2 right-0 bottom-0 left-1/2 bg-indigo-50 -z-10" />
              )}
              {isRangeEnd && !isSingleDayRange && (
                <div className="absolute top-1/2 -translate-y-1/2 left-0 bottom-0 right-1/2 bg-indigo-50 -z-10" />
              )}

              {/* Day number */}
              <span className={cn(
                "font-medium relative z-10 leading-tight",
                isMobile ? "text-xs" : "text-sm"
              )}>
                {formattedDate}
              </span>

              {/* Price tag - smaller and more compact */}
              {priceDisplay && (
                <span className={cn(
                  "leading-none font-medium relative z-10",
                  isMobile ? "text-[7px]" : "text-[8px]",
                  isPartialFromDate || isSelected
                    ? "text-white/80"
                    : isInRange
                      ? "text-indigo-600"
                      : showHoverPreview
                        ? "text-indigo-500"
                        : "text-gray-400"
                )}>
                  {priceDisplay}
                </span>
              )}
            </button>
          </div>
        );
        day = new Date(day.setDate(day.getDate() + 1));
      }

      days.push(
        <div key={day.toString()} className="grid grid-cols-7 gap-1 sm:gap-2">
          {weekDaysArray}
        </div>
      );
    }

    return (
      <div className="w-full">
        {/* Month header */}
        <div className="text-center mb-2">
          <h2 className="text-sm font-semibold text-gray-700">
            {format(monthDate, "MMMM yyyy")}
          </h2>
        </div>

        {/* Week day headers */}
        <div className="grid grid-cols-7 mb-1">
          {weekDays.map((d, i) => (
            <div
              key={i}
              className={cn(
                "text-center font-medium",
                isMobile ? "text-[9px] text-gray-400 uppercase py-0.5" : "text-[10px] text-gray-400 uppercase tracking-wide py-1",
                (i === 0 || i === 6) && "text-red-400"
              )}
            >
              {d}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="space-y-0.5">
          {days}
        </div>
      </div>
    );
  };

  return (
    <div className={cn("bg-white rounded-xl shadow-lg border border-gray-100 p-2 sm:p-3 relative", className)}>
      {/* Navigation */}
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <button
          onClick={prevMonth}
          className="h-6 w-6 sm:h-7 sm:w-7 bg-white border border-gray-200 rounded-md flex items-center justify-center hover:bg-gray-50 hover:border-indigo-300 transition-all duration-150 z-10"
        >
          <ChevronLeft className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-600" />
        </button>

        <div className="text-xs sm:text-sm font-medium text-gray-600">
          {format(currentMonth, "MMMM yyyy")}
        </div>

        <button
          onClick={nextMonth}
          className="h-6 w-6 sm:h-7 sm:w-7 bg-white border border-gray-200 rounded-md flex items-center justify-center hover:bg-gray-50 hover:border-indigo-300 transition-all duration-150 z-10"
        >
          <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-600" />
        </button>
      </div>

      {/* Calendar grid */}
      <div className={cn(
        "grid gap-2 sm:gap-4",
        isMobile ? "grid-cols-1" : "sm:grid-cols-2"
      )}>
        {months.map((month, i) => (
          <div key={i} className="w-full">
            {renderMonth(month, i)}
          </div>
        ))}
      </div>
    </div>
  );
}

export { Calendar };
